// DashboardsHomePage — layout shell for `/dashboards/*`. Owns the
// Dashboards/Templates tab toggle and renders the matched child via
// `<Outlet />`. Selection lives on path segments:
//
//   /dashboards/<path>
//   /dashboards/f/<folderPath>
//   /dashboards/templates/<path>
//   /dashboards/templates/f/<folderPath>
//
// The tab strip is forwarded into each sub-page as a `topSlot` so
// it ends up rendered ABOVE the sidebar header (rather than across
// the whole page width).

import { useEffect, useMemo, type ReactNode } from 'react'
import { LayoutDashboard, LayoutTemplate } from 'lucide-react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { ExplorerTabs, type ExplorerTab } from '../../components/explorer'
import { DASHBOARDS_HOME, TEMPLATES_HOME } from './explorer-paths'

export type ExplorerView = 'dashboards' | 'templates'

export interface DashboardsOutletContext {
  tabsSlot: ReactNode
}

const DASHBOARD_TABS: ReadonlyArray<ExplorerTab<ExplorerView>> = [
  { id: 'dashboards', label: 'Dashboards', icon: LayoutDashboard },
  { id: 'templates', label: 'Templates', icon: LayoutTemplate }
]

function readView(pathname: string): ExplorerView {
  return pathname === TEMPLATES_HOME || pathname.startsWith(`${TEMPLATES_HOME}/`)
    ? 'templates'
    : 'dashboards'
}

const DashboardsHomePage = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const view = readView(pathname)

  // Legacy `?view=templates` → `/dashboards/templates` (keep other params
  // so `?template=` / `?folder=` can still be rewritten by TemplatesPage).
  useEffect(() => {
    if (searchParams.get('view') !== 'templates') return
    if (pathname === TEMPLATES_HOME || pathname.startsWith(`${TEMPLATES_HOME}/`)) return
    const next = new URLSearchParams(searchParams)
    next.delete('view')
    const search = next.toString()
    navigate(
      { pathname: TEMPLATES_HOME, search: search === '' ? '' : `?${search}` },
      { replace: true }
    )
  }, [searchParams, pathname, navigate])

  const tabsSlot = useMemo(
    () => (
      <ExplorerTabs
        tabs={DASHBOARD_TABS}
        active={view}
        onChange={(next) => {
          navigate(next === 'templates' ? TEMPLATES_HOME : DASHBOARDS_HOME)
        }}
        testIdPrefix="explorer-tab"
      />
    ),
    [view, navigate]
  )

  return <Outlet context={{ tabsSlot } satisfies DashboardsOutletContext} />
}

export default DashboardsHomePage
